import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '../components/Button';
import { Teams } from '../components/Teams';
import { Content } from '../components/Content';
import {
  Draft as DraftType,
  TeamColorsValues,
  Team,
  TeamColors,
  GuessModes,
  GuessModesValues,
} from '../common.type';
import { Description } from '../components/Description';
import { newDraft } from '../helpers/draft';
import { Buffer } from 'buffer';
import { Context } from '../components/Context';
import { ButtonToggle } from '../components/ButtonToggle';
import { copy } from '../helpers/object';
import { Tries } from '../components/Tries';
import { compress } from '../helpers/compress';
import { useIndexedDB } from 'react-indexed-db-hook';
import { DbDraft } from '../type';
import { TextCSS } from '../components/Text';
import { Share } from '../components/Share';

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2vmin;
  align-items: center;
`;
const TeamsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3vmin;
  margin-top: 3vmin;
`;
const Link = styled.div`
  display: flex;
  flex-direction: row;
`;
const Input = styled.input`
  padding: ${(props) => `1${props.theme.unit}`};
  border: 0;
  border-radius: 0.5vmin 0 0 0.5vmin;
`;
const Copy = styled.button`
  cursor: pointer;
  padding: ${(props) => `1${props.theme.unit} 1.5${props.theme.unit}`};
  font-size: ${(props) => `2${props.theme.unit}`};
  border: 0;
  border-radius: ${(props) => `0.5${props.theme.unit}`};
  border-radius: ${(props) =>
    `0 0.5${props.theme.unit} 0.5${props.theme.unit} 0`};
`;
const Actions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2vmin;
`;
const ButtonSettings = styled.button`
  cursor: pointer;
  text-shadow: 0 0.2vmin #000;
  background: transparent;
  border: 0;
  padding: 0;
  font-size: ${(props) => `5${props.theme.unit}`};
`;

const Coin = styled.div<{ color: TeamColorsValues }>`
  width: ${(props) => `5${props.theme.unit}`};
  height: ${(props) => `5${props.theme.unit}`};
  background: ${(props) =>
    `url(${process.env.PUBLIC_URL}/images/coin-${props.color}.webp) no-repeat`};
  background-size: contain;
`;
const SettingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5vmin;
  justify-content: center;
  align-items: center;
`;
const Label = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5vmin;
  ${TextCSS({})}
`;
const Img = styled.img`
  width: ${(props) => `3.5${props.theme.unit}`};
`;

const draftInit: DraftType = newDraft();

export const Draft = () => {
  const intl = useIntl();
  const { add } = useIndexedDB('draftList');
  const [draft, setDraft] = useState<DraftType>(draftInit);
  const [lock, setLock] = useState<boolean>(false);
  const [token, setToken] = useState<string | undefined>();
  const [openSettings, setOpenSettings] = useState<boolean>(false);

  const disabled = useMemo(() => {
    return (
      Object.values(draft.blueTeam).findIndex(
        (p) => p.id === null || p.ban === null
      ) !== -1 ||
      Object.values(draft.redTeam).findIndex(
        (p) => p.id === null || p.ban === null
      ) !== -1
    );
  }, [draft.blueTeam, draft.redTeam]);

  const validate = useCallback(() => {
    setLock(true);
    const draftCompressed = compress(draft);
    setToken(Buffer.from(draftCompressed).toString('base64'));
    add<DbDraft>({
      data: draftCompressed,
      dateCreation: new Date(),
    }).catch((err: unknown) => {
      console.error(err);
    });
  }, [draft]);

  const copyLink = useCallback(() => {
    if (!token) return;
    const url = `${process.env.REACT_APP_URL}/draft/guess/${token}`;
    navigator.clipboard.writeText(url).catch((err: unknown) => {
      console.error(err);
    });
  }, [token]);

  return (
    <Content gap={0}>
      {token ? (
        <Main>
          <Description>
            <FormattedMessage id="draftCreate" />
          </Description>

          <Link>
            <Input
              type="text"
              readOnly
              value={`${process.env.REACT_APP_URL}/draft/guess/${token}`}
            />
            <Copy onClick={copyLink}>
              <FormattedMessage id="copy" />
            </Copy>
          </Link>
          <Share
            data={{
              text: intl.formatMessage({ id: 'draftShareText' }),
              url: `${process.env.REACT_APP_URL}/draft/guess/${token}`,
            }}
          />
        </Main>
      ) : (
        <>
          <Description>
            <FormattedMessage id="draftDescription" />
          </Description>
          <TeamsWrapper>
            <Teams
              blueTeam={draft.blueTeam}
              redTeam={draft.redTeam}
              settings={draft.settings}
              mapId={draft.mapId}
              updateTeams={(teams: { blueTeam: Team; redTeam: Team }) => {
                const d: DraftType = {
                  ...copy(draft),
                  blueTeam: { ...teams.blueTeam },
                  redTeam: { ...teams.redTeam },
                };
                setDraft(d);
              }}
              updateMapId={(mapId: number) => {
                setDraft({ ...copy(draft), mapId });
              }}
            />
          </TeamsWrapper>
          <Tries current={0} max={draft.settings.tries}></Tries>
          <Actions>
            <ButtonSettings
              onClick={() => {
                setOpenSettings(true);
              }}
            >
              ⚙️
            </ButtonSettings>
            <Button
              onClick={validate}
              color="green"
              disabled={disabled || lock}
            >
              <FormattedMessage id={'validate'} />
            </Button>
          </Actions>
          {openSettings && (
            <Context
              onClose={() => {
                setOpenSettings(false);
              }}
            >
              <SettingWrapper>
                <Label>
                  <FormattedMessage id="draftSettingsCoin" />
                </Label>
                <ButtonToggle<TeamColorsValues>
                  value={draft.settings.coin}
                  values={[
                    {
                      value: TeamColors.BLUE,
                      children: <Coin color={TeamColors.BLUE}></Coin>,
                    },
                    {
                      value: TeamColors.RED,
                      children: <Coin color={TeamColors.RED}></Coin>,
                    },
                  ]}
                  onClick={(color: TeamColorsValues) => {
                    setDraft({
                      ...draft,
                      settings: { ...draft.settings, coin: color },
                    });
                  }}
                />
              </SettingWrapper>
              <SettingWrapper>
                <Label>
                  <FormattedMessage id="draftSettingsTries" />
                </Label>
                <Tries
                  current={draft.settings.tries}
                  max={8}
                  onClick={(tries) => {
                    setDraft({
                      ...draft,
                      settings: { ...draft.settings, tries },
                    });
                  }}
                ></Tries>
              </SettingWrapper>
              <SettingWrapper>
                <Label>
                  <FormattedMessage id="draftSettingsMode" />
                  <Img
                    src={`${process.env.PUBLIC_URL}/images/eye.webp`}
                    alt=""
                  />
                </Label>
                <ButtonToggle<GuessModesValues>
                  value={draft.settings.mode}
                  values={[
                    {
                      value: GuessModes.LAST,
                      children: <FormattedMessage id="guessLast" />,
                    },
                    {
                      value: GuessModes.TWO_LAST,
                      children: <FormattedMessage id="guessTwoLast" />,
                    },
                  ]}
                  onClick={(value: GuessModesValues) => {
                    setDraft({
                      ...draft,
                      settings: { ...draft.settings, mode: value },
                    });
                  }}
                />
              </SettingWrapper>
            </Context>
          )}
        </>
      )}
    </Content>
  );
};
